import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "reduxFiles/hooks"; // ← مسیر رو تنظیم کن
import {
  FaPaperPlane,
  FaCheckCircle,
  FaUser,
  FaBuilding,
  FaPhone,
  FaEnvelope,
  FaCommentDots,
  FaSpinner,
} from "react-icons/fa";
import { useCreateRequestMutation } from "reduxFiles/api/userApi";

const ContactForm = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState("idle");
  const [submitContact] = useCreateRequestMutation();

  const profile = useAppSelector((state) => state.auth.user);

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setStatus("loading");

    const form = e.currentTarget;
    const formData = new FormData(form);

    const payload = {
      fullname: String(formData.get("fullName") || ""),
      companyname: String(formData.get("orgName") || ""),
      mobile: String(formData.get("phone") || ""),
      email: String(formData.get("email") || ""),
      description: String(formData.get("message") || ""),
    };

    try {
      await submitContact(payload).unwrap();
      setStatus("success");
      form.reset();
    } catch (err) {
      setStatus("error");
    }
  };

  const inputClass =
    "peer w-full rounded-xl border border-gray-200 bg-white px-4 py-3.5 pl-11 text-sm text-navy-700 placeholder-gray-400 " +
    "hover:border-brand-300 hover:bg-gray-50 " +
    "focus:border-brand-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/15 " +
    "transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed dark:bg-navy-800 dark:border-navy-700 dark:text-white";

  const lockedInputClass =
    "peer w-full rounded-xl border border-gray-200 bg-gray-100 px-4 py-3.5 pl-11 text-sm text-gray-500 placeholder-gray-400 " +
    "cursor-not-allowed dark:bg-navy-900 dark:border-navy-700 dark:text-gray-400";

  const labelClass =
    "mb-2 flex items-center gap-1.5 text-[13px] font-medium text-navy-700 dark:text-gray-300";

  const iconClass =
    "pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500 " +
    "peer-focus:text-brand-500 peer-hover:text-gray-500 transition-colors";

  const isLoading = status === "loading";

  return (
    <div className="relative h-full overflow-hidden rounded-3xl border border-gray-200 bg-white p-6 text-right shadow-[0_20px_60px_rgba(112,144,176,0.08)] dark:border-navy-700 dark:bg-navy-800 sm:p-8">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-brand-500/5 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-32 -left-32 h-72 w-72 rounded-full bg-cyan-500/5 blur-3xl"
      />

      <div className="relative">
        {/* Header */}
        <div className="mb-7 flex items-center justify-between border-b border-gray-100 pb-5 dark:border-navy-700">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-cyan-100 bg-cyan-50 text-cyan-600 dark:border-cyan-800 dark:bg-cyan-900/20">
              <FaCommentDots className="h-5 w-5" />
            </div>
            <h3 className="text-base font-bold text-navy-700 dark:text-white sm:text-lg">
              {t("contact.form.title")}
            </h3>
          </div>
        </div>

        {status === "success" ? (
          <div className="space-y-5 py-14 text-center">
            <div className="relative inline-flex">
              <div className="animate-pulse absolute inset-0 rounded-full bg-cyan-500/15 blur-xl" />
              <div className="relative flex h-20 w-20 items-center justify-center rounded-full border border-cyan-100 bg-cyan-50 dark:border-cyan-800 dark:bg-cyan-900/20">
                <FaCheckCircle className="h-12 w-12 text-cyan-600" />
              </div>
            </div>
            <h4 className="text-xl font-bold text-navy-700 dark:text-white">
              {t("contact.form.successTitle")}
            </h4>
            <p className="mx-auto max-w-md text-sm leading-relaxed text-gray-700 dark:text-gray-400">
              {t("contact.form.successDesc")}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="fullName" className={labelClass}>
                  <FaUser className="h-3.5 w-3.5 text-cyan-600" />
                  {t("contact.form.fullNameLabel")}
                </label>
                <div className="relative">
                  <FaUser className={iconClass} />
                  <input
                    id="fullName"
                    name="fullName"
                    required
                    disabled={isLoading}
                    type="text"
                    defaultValue={profile?.fullname || profile?.name || ""}
                    readOnly
                    placeholder={t("contact.form.fullNamePlaceholder")}
                    className={lockedInputClass}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="orgName" className={labelClass}>
                  <FaBuilding className="h-3.5 w-3.5 text-cyan-600" />
                  {t("contact.form.orgLabel")}
                </label>
                <div className="relative">
                  <FaBuilding className={iconClass} />
                  <input
                    id="orgName"
                    name="orgName"
                    required
                    disabled={isLoading}
                    type="text"
                    placeholder={t("contact.form.orgPlaceholder")}
                    className={inputClass}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="phone" className={labelClass}>
                  <FaPhone className="h-3.5 w-3.5 text-cyan-600" />
                  {t("contact.form.phoneLabel")}
                </label>
                <div className="relative">
                  <FaPhone className={iconClass} />
                  <input
                    id="phone"
                    name="phone"
                    required
                    disabled={isLoading}
                    type="tel"
                    defaultValue={profile?.mobile || profile?.phone || ""}
                    readOnly
                    placeholder={t("contact.form.phonePlaceholder")}
                    className={lockedInputClass}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className={labelClass}>
                  <FaEnvelope className="h-3.5 w-3.5 text-cyan-600" />
                  {t("contact.form.emailLabel")}
                </label>
                <div className="relative">
                  <FaEnvelope className={iconClass} />
                  <input
                    id="email"
                    name="email"
                    required
                    disabled={isLoading}
                    type="email"
                    defaultValue={profile?.email || ""}
                    readOnly
                    placeholder={t("contact.form.emailPlaceholder")}
                    className={lockedInputClass}
                  />
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="message" className={labelClass}>
                <FaCommentDots className="h-3.5 w-3.5 text-cyan-600" />
                {t("contact.form.messageLabel")}
              </label>
              <textarea
                id="message"
                name="message"
                rows={5}
                disabled={isLoading}
                placeholder={t("contact.form.messagePlaceholder")}
                className="w-full resize-none rounded-xl border border-gray-200 bg-white px-4 py-3.5 text-sm leading-relaxed text-navy-700 placeholder-gray-400 transition-all duration-200 hover:border-brand-300 hover:bg-gray-50 focus:border-brand-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/15 disabled:cursor-not-allowed disabled:opacity-60 dark:border-navy-700 dark:bg-navy-800 dark:text-white"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full overflow-hidden rounded-xl bg-gradient-to-r from-brand-500 to-brand-600 py-4 text-sm font-bold text-white shadow-[0_10px_30px_rgba(66,42,251,0.22)] transition-all duration-300 hover:shadow-[0_15px_40px_rgba(66,42,251,0.32)] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-70"
            >
              <span
                aria-hidden="true"
                className="from-transparent to-transparent absolute inset-0 -translate-x-full bg-gradient-to-r via-white/20 transition-transform duration-700 group-hover:translate-x-full"
              />
              <span className="relative flex items-center justify-center gap-2">
                {isLoading ? (
                  <>
                    <FaSpinner className="animate-spin h-4 w-4" />
                    <span>{t("contact.form.submitBtn")}</span>
                  </>
                ) : (
                  <>
                    <FaPaperPlane className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    <span>{t("contact.form.submitBtn")}</span>
                  </>
                )}
              </span>
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ContactForm;
